import { useState, useCallback } from 'react';
import { getAllCategories } from '../../../services/websocketApi/categories';
import { getInstrumentsLength } from '../../../services/websocketApi/markets';
import { getInstrumentsByIndex } from '../../../services/websocketApi/instruments';

// Number of instruments to show per page
export const ITEMS_PER_PAGE = 20;

export default function useMarketData(isWebSocketReady) {
  const [instrumentCount, setInstrumentCount] = useState(0);
  const [categories, setCategories] = useState([{ id: 'all', name: 'All' }]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);
  const [pageInstruments, setPageInstruments] = useState([]);

  // Fetch categories and instrument count
  const fetchData = async () => {
    // Check WebSocket connection status
    if (!isWebSocketReady) {
      setError('WebSocket connection not ready. Please wait for connection or refresh.');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      // Get categories from WebSocket API
      let categoryData;
      try {
        categoryData = await getAllCategories();
      } catch (catError) {
        // Use default categories if API fails
        categoryData = { tabs: ['forex'] };
      }
      
      if (categoryData && categoryData.tabs) {
        // Format categories for UI
        const formattedCategories = [
          { id: 'all', name: 'All' },
          ...categoryData.tabs.map(cat => ({
            id: cat,
            name: cat.charAt(0).toUpperCase() + cat.slice(1)
          }))
        ];
        
        setCategories(formattedCategories);
        
        // Get instruments count for the current category
        try {
          const count = await getInstrumentsLength(selectedCategory !== 'all' ? selectedCategory : null);
          setInstrumentCount(count);
          // Reset page to 1 when new data is loaded
          setCurrentPage(1);
          
          // Fetch first page of instruments
          await fetchPageInstruments(1, count, selectedCategory !== 'all' ? selectedCategory : null);
        } catch (countError) {
          console.error('Error fetching instrument count:', countError);
          // Only set a minimal fallback if we absolutely can't get a count
          setInstrumentCount(1);
          setPageInstruments([]);
        }
      } else {
        // Set default categories if API returns invalid data
        setCategories([
          { id: 'all', name: 'All' },
          { id: 'forex', name: 'Forex' }
        ]);
        
        // Since categories failed, try to get instrument count separately
        try {
          const count = await getInstrumentsLength(null);
          setInstrumentCount(count);
          
          // Fetch first page of instruments
          await fetchPageInstruments(1, count, null);
        } catch (countError) {
          // Last resort fallback
          setInstrumentCount(1);
          setPageInstruments([]);
        }
      }
    } catch (err) {
      setError('Failed to load market data. Please try again.');
      // No hardcoded fallback
      setInstrumentCount(0);
      setPageInstruments([]);
    } finally {
      setLoading(false);
    }
  };
  
  // Fetch instruments for a specific page
  const fetchPageInstruments = async (page, totalCount, category = null) => {
    if (!isWebSocketReady) return;
    
    const start = (page - 1) * ITEMS_PER_PAGE;
    // Calculate how many instruments to fetch (don't exceed total count)
    const count = Math.min(ITEMS_PER_PAGE, totalCount - start);
    
    if (count <= 0) {
      setPageInstruments([]);
      return;
    }
    
    // Set loading state while fetching
    setLoading(true);
    setError(null);
    
    try {
      // Create filter object if category is provided
      const filter = category ? { category } : {};
      
      // First try to fetch all instruments in a single request
      try {
        const instruments = await getInstrumentsByIndex(start, count, filter);
        if (instruments && Array.isArray(instruments)) {
          // Format instruments for display
          const formattedInstruments = instruments.map((instrument, index) => {
            // Create a unique ID by combining symbol and index to avoid React key warnings
            const uniqueId = instrument.symbol ? 
              `${instrument.symbol}-${index}-${start}` : 
              `instrument-${start + index}-${Date.now()}`;
            
            return {
              originalId: instrument.symbol || null, // Keep original ID for API calls
              id: uniqueId,  // Use unique ID for React keys
              name: instrument.description || instrument.symbol || 'Instrument',
              price: typeof instrument.price === 'number' ? instrument.price : 0,
              bidPrice: typeof instrument.bid === 'number' ? instrument.bid : null,
              askPrice: typeof instrument.ask === 'number' ? instrument.ask : null,
              change: typeof instrument['24hour-change'] === 'number' ? instrument['24hour-change'] : 0,
              category: instrument.category || category || 'forex',
              volume: instrument.volume || 'N/A'
            };
          });
          
          setPageInstruments(formattedInstruments);
          setError(null);
          setLoading(false);
          
          // Return formatted instruments for subscription
          return formattedInstruments;
        }
      } catch (singleRequestError) {
        // Continue to batch mode if the single request fails
      }
      
      // Fallback: Fetch batch of instruments for this page if the single request failed
      const batchSize = 5; // Fetch 5 instruments at a time to avoid timeouts
      let allInstruments = [];
      let fetchErrors = 0;
      
      // Use smaller batches to avoid timeout
      for (let batchStart = start; batchStart < start + count; batchStart += batchSize) {
        const batchCount = Math.min(batchSize, start + count - batchStart);
        
        let retryCount = 0;
        const maxRetries = 2;
        let success = false;
        
        // Retry this batch a few times before giving up
        while (!success && retryCount <= maxRetries) {
          try {
            const instruments = await getInstrumentsByIndex(batchStart, batchCount, filter);
            if (instruments && Array.isArray(instruments)) {
              allInstruments = [...allInstruments, ...instruments];
              success = true;
            } else {
              throw new Error('Invalid response format');
            }
          } catch (batchError) {
            retryCount++;
            if (retryCount <= maxRetries) {
              // Exponential backoff between retries
              await new Promise(resolve => setTimeout(resolve, 500 * Math.pow(2, retryCount - 1)));
            } else {
              fetchErrors++;
            }
          }
        }
        
        // Add a small delay between batches to prevent overwhelming the server
        await new Promise(resolve => setTimeout(resolve, 100));
      }
      
      // Even if some batches fail, proceed with any instruments we received
      if (allInstruments.length > 0) {
        // Format instruments for display
        const formattedInstruments = allInstruments.map((instrument, index) => {
          // Create a unique ID by combining symbol and index to avoid React key warnings
          const uniqueId = instrument.symbol ? 
            `${instrument.symbol}-${index}-${start}` : 
            `instrument-${start + index}-${Date.now()}`;
          
          return {
            originalId: instrument.symbol || null, // Keep original ID for API calls
            id: uniqueId,  // Use unique ID for React keys
            name: instrument.description || instrument.symbol || 'Instrument',
            price: typeof instrument.price === 'number' ? instrument.price : 0,
            bidPrice: typeof instrument.bid === 'number' ? instrument.bid : null,
            askPrice: typeof instrument.ask === 'number' ? instrument.ask : null,
            change: typeof instrument['24hour-change'] === 'number' ? instrument['24hour-change'] : 0,
            category: instrument.category || category || 'forex',
            volume: instrument.volume || 'N/A'
          };
        });
        
        setPageInstruments(formattedInstruments);
        
        // Only show error if some batches failed but we still have some data
        if (fetchErrors > 0) {
          setError(`Some data could not be loaded. Showing partial results.`);
        } else {
          setError(null);
        }
        
        // Return formatted instruments for subscription
        return formattedInstruments;
      } else {
        // If all batches failed, generate placeholder data so UI is not empty
        const placeholders = Array.from({ length: count }, (_, i) => ({
          id: `placeholder-${start + i}-${Date.now()}`,
          name: `Instrument #${start + i}`,
          price: Math.random() * 100, // Random price
          bidPrice: Math.random() * 100,
          askPrice: Math.random() * 100 + 1,
          change: (Math.random() * 10) - 5, // Random change between -5% and +5%
          category: category || 'unknown',
          volume: 'N/A',
          isPlaceholder: true
        }));
        
        setPageInstruments(placeholders);
        setError('Could not load real instrument data. Showing placeholders.');
        return placeholders;
      }
    } catch (err) {
      // Generate placeholder data as a last resort
      const placeholders = Array.from({ length: count }, (_, i) => ({
        id: `error-placeholder-${start + i}-${Date.now()}`,
        name: `Instrument #${start + i}`,
        price: Math.random() * 100,
        bidPrice: Math.random() * 100,
        askPrice: Math.random() * 100 + 1,
        change: (Math.random() * 10) - 5,
        category: category || 'unknown',
        volume: 'N/A',
        isPlaceholder: true
      }));
      
      setPageInstruments(placeholders);
      setError(`Failed to load instruments. Showing placeholders.`);
      return placeholders;
    } finally {
      setLoading(false);
    }
  };
  
  // Fetch instrument count for a specific category
  const fetchInstrumentCountForCategory = async (category) => {
    // Check WebSocket connection status
    if (!isWebSocketReady) {
      setError('WebSocket connection not ready. Please wait for connection or refresh.');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      // Get instruments count for the selected category
      const count = await getInstrumentsLength(category !== 'all' ? category : null);
      setInstrumentCount(count);
      // Reset to first page when changing category
      setCurrentPage(1);
      
      // Fetch first page of instruments for the new category
      const instruments = await fetchPageInstruments(1, count, category !== 'all' ? category : null);
      return instruments;
    } catch (err) {
      console.error(`Error fetching ${category} instrument count:`, err);
      setError(`Failed to load ${category} markets. Please try again.`);
      // Minimal fallback
      setInstrumentCount(0);
      setPageInstruments([]);
      return [];
    } finally {
      setLoading(false);
    }
  };

  // Handle category change
  const handleCategoryChange = async (category) => {
    setSelectedCategory(category);
    return await fetchInstrumentCountForCategory(category);
  };

  // Handle search input
  const handleSearchChange = (e) => {
    setSearchQuery(e.target.value);
    // Reset to first page when searching
    setCurrentPage(1);
  };

  // Handle pagination
  const handlePageChange = async (newPage) => {
    setCurrentPage(newPage);
    
    // Fetch instruments for the new page
    const instruments = await fetchPageInstruments(newPage, instrumentCount, selectedCategory !== 'all' ? selectedCategory : null);
    
    // Scroll to top of the list
    const listElement = document.querySelector('.markets-list');
    if (listElement) {
      listElement.scrollTop = 0;
    }
    
    return instruments;
  };

  // Calculate total pages
  const totalPages = instrumentCount > 0 ? Math.ceil(instrumentCount / ITEMS_PER_PAGE) : 1;
  
  // Calculate start and end indices for current page
  const startIndex = (currentPage - 1) * ITEMS_PER_PAGE;
  const endIndex = Math.min(startIndex + ITEMS_PER_PAGE, instrumentCount);

  // Filter page instruments based on search query if needed
  const filteredInstruments = searchQuery
    ? pageInstruments.filter(instrument => 
        instrument.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
        (instrument.id && instrument.id.toLowerCase().includes(searchQuery.toLowerCase()))
      )
    : pageInstruments;

  return {
    instrumentCount,
    categories,
    loading,
    error,
    searchQuery,
    selectedCategory,
    currentPage,
    pageInstruments,
    filteredInstruments,
    totalPages,
    startIndex,
    endIndex,
    fetchData,
    fetchPageInstruments,
    handleCategoryChange,
    handleSearchChange,
    handlePageChange,
    setPageInstruments
  };
} 